import { env, isDevBuild, isEnvMode, type EnvMode } from "@/config/env";
import { isNonEmptyString, isNullableString, isRecord } from "@/lib/typeGuards";

export type HeaderMode = "native" | "web";

export interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * Data the native shell injects with `injectedJavaScriptBeforeContentLoaded`,
 * i.e. before any application script runs.
 *
 * What travels here is a one-time exchange code, never the customer's access
 * token: the page trades the code for a short-lived, scoped web session, so a
 * compromised page cannot walk away with the real credential.
 *
 * It travels through injection rather than the URL because a query string ends
 * up in nginx access logs, in the `Referer` header of any outbound request and
 * in crash reports. This object only ever lives in WebView memory.
 *
 * Note there is no API base URL here — which host to talk to belongs to the
 * build (VITE_API_BASE_URL, see src/config/env.ts) so that it has exactly one
 * source. That host is a different origin from the bundle's, which is why the
 * session is a bearer token rather than a cookie (src/api/tokenStore.ts).
 */
export interface Bootstrap {
  v: 1;
  /** One-time code for POST /v1/webview/session/exchange. */
  exchangeCode: string | null;
  env: EnvMode;
  appVersion: string;
  platform: "ios" | "android";
  /**
   * Who draws the page header. Today the native stack draws it; the web app
   * reads this so the same code keeps working when that flips.
   */
  headerMode: HeaderMode;
  insets: SafeAreaInsets;
}

export type BootstrapSource = "native" | "browser-dev";

export type BootstrapResult =
  | { ok: true; source: BootstrapSource; data: Bootstrap }
  | { ok: false; reason: string };

const NO_INSETS: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };

function isSafeAreaInsets(value: unknown): value is SafeAreaInsets {
  return (
    isRecord(value) &&
    typeof value["top"] === "number" &&
    typeof value["right"] === "number" &&
    typeof value["bottom"] === "number" &&
    typeof value["left"] === "number"
  );
}

export function isBootstrap(value: unknown): value is Bootstrap {
  return (
    isRecord(value) &&
    value["v"] === 1 &&
    // A bootstrap carrying `accessToken` comes from an app version that
    // predates the code exchange. Rejecting it here is deliberate: that shell
    // would hand this page the real credential.
    value["accessToken"] === undefined &&
    isNullableString(value["exchangeCode"]) &&
    isEnvMode(value["env"]) &&
    isNonEmptyString(value["appVersion"]) &&
    (value["platform"] === "ios" || value["platform"] === "android") &&
    (value["headerMode"] === "native" || value["headerMode"] === "web") &&
    isSafeAreaInsets(value["insets"])
  );
}

function developmentFallback(): Bootstrap {
  return {
    v: 1,
    exchangeCode: null,
    env: env.envMode,
    appVersion: "0.0.0-dev",
    platform: "android",
    headerMode: "web",
    insets: { ...NO_INSETS },
  };
}

/**
 * Reads and validates the injected bootstrap.
 *
 * In `vite dev` with no native shell present it falls back to a synthetic
 * bootstrap so the app can be opened in a normal browser tab. That fallback is
 * unreachable in a production build.
 */
export function readBootstrap(): BootstrapResult {
  const raw: unknown = window.__SGC_BOOTSTRAP__;

  if (raw === undefined) {
    if (isDevBuild) {
      return { ok: true, source: "browser-dev", data: developmentFallback() };
    }
    return {
      ok: false,
      reason: "No bootstrap was injected by the native shell.",
    };
  }

  if (!isBootstrap(raw)) {
    return {
      ok: false,
      reason: "The injected bootstrap does not match the expected shape.",
    };
  }

  return { ok: true, source: "native", data: raw };
}
